import React, { useState, useEffect } from 'react';
import { Grid, Box, Typography } from '@mui/material';
import {
    Select,
    DateInput,
    LoadingBar,
} from '../../../components/Common';
import MultiSelect from '../../../components/Common/MultiSelect';
import SummaryTable from './SummaryTable';
import ResultsTable from './ResultsTable';
import Routes from '../../../routes';

const InstalledInventoryEmissionsExposure = () => {
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);

    // Filter states - store as array of values (["-1"] = All)
    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 3);
        return date;
    });
    const [endDate, setEndDate] = useState(new Date());
    const [locationNames, setLocationNames] = useState(["-1"]);
    const [materialTypes, setMaterialTypes] = useState(["-1"]);
    const [locationTypes, setLocationTypes] = useState(["-1"]);
    const [regionDivisions, setRegionDivisions] = useState(["-1"]);
    const [stateFilters, setStateFilters] = useState(["-1"]);
    const [gwpValue, setGwpValue] = useState('AR4');
    const [applicableRegulations, setApplicableRegulations] = useState(["-1"]);
    const [classifications, setClassifications] = useState(["-1"]);

    // Filter options from API
    const [locationOptions, setLocationOptions] = useState([]);
    const [materialTypeOptions, setMaterialTypeOptions] = useState([]);
    const [locationTypeOptions, setLocationTypeOptions] = useState([]);
    const [regionDivisionOptions, setRegionDivisionOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [classificationOptions, setClassificationOptions] = useState([]);
    const [applicableRegulationOptions, setApplicableRegulationOptions] = useState([]);
    const [gwpOptions, setGwpOptions] = useState([]);

    // Date validation handlers
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    const handleStartDateChange = (newDate) => {
        if (!newDate || isNaN(newDate.getTime())) return;
        if (newDate > today) return;
        if (endDate && newDate > endDate) return;
        setStartDate(newDate);
    };

    const handleEndDateChange = (newDate) => {
        if (!newDate || isNaN(newDate.getTime())) return;
        if (newDate > today) return;
        if (startDate && newDate < startDate) return;
        setEndDate(newDate);
    };

    // Report data
    const [summaryData, setSummaryData] = useState({
        startWeightLbs: 0,
        startWeightMt: 0,
        endWeightLbs: 0,
        endWeightMt: 0,
        differenceLbs: 0,
        differenceMt: 0,
        potentialEmissions: 0,
    });
    const [reportData, setReportData] = useState([]);

    // Fetch filter options when dates change
    useEffect(() => {
        fetchFilterOptions();
    }, [startDate, endDate]);

    // Fetch report data when filters change
    useEffect(() => {
        fetchReportData();
    }, [startDate, endDate, gwpValue, locationNames, materialTypes, locationTypes, regionDivisions, stateFilters, classifications, applicableRegulations]);

    const fetchFilterOptions = async () => {
        try {
            // Pass dates as query params to filter options based on date range
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate.toISOString());
            if (endDate) params.append('endDate', endDate.toISOString());
            const url = `${Routes.PowerBI.IIEETableFilters()}?${params.toString()}`;

            const response = await fetch(url);
            if (!response.ok) throw new Error('Failed to fetch filter options');

            const data = await response.json();

            // Group filters by property and add "All" option at the beginning
            const allOption = { value: "-1", name: 'All' };
            const locationFilters = [allOption, ...data.filter(f => f.property === 'locationName').map(f => ({ value: f.value, name: f.name }))];
            const materialFilters = [allOption, ...data.filter(f => f.property === 'materialType').map(f => ({ value: f.value, name: f.name }))];
            const regionFilters = [allOption, ...data.filter(f => f.property === 'regionDivision').map(f => ({ value: f.value, name: f.name }))];
            const stateFiltersList = [allOption, ...data.filter(f => f.property === 'state').map(f => ({ value: f.value, name: f.name }))];
            const classFilters = [allOption, ...data.filter(f => f.property === 'classification').map(f => ({ value: f.value, name: f.name }))];
            const noneOption = { value: "none", name: 'None' };
            const regulationFilters = [allOption, noneOption, ...data.filter(f => f.property === 'applicableRegulation').map(f => ({ value: f.value, name: f.name }))];
            const locationTypeFilters = [allOption, ...data.filter(f => f.property === 'locationType').map(f => ({ value: f.value, name: f.name }))];
            const gwpFilters = data.filter(f => f.property === 'gwpValue').map(f => ({ id: f.value, label: f.name }));

            setLocationOptions(locationFilters);
            setMaterialTypeOptions(materialFilters);
            setLocationTypeOptions(locationTypeFilters);
            setRegionDivisionOptions(regionFilters);
            setStateOptions(stateFiltersList);
            setClassificationOptions(classFilters);
            setApplicableRegulationOptions(regulationFilters);
            setGwpOptions(gwpFilters);

            setLoading(false);
        } catch (error) {
            console.error('Error fetching filter options:', error);
            setLoading(false);
        }
    };


    // Handler for MultiSelect changes - manages "All" option behavior
    const handleMultiSelectChange = (setter) => (e) => {
        const newValue = e.target.value;
        const wasAllSelected = newValue.length > 1 && newValue[0] === "-1";
        const isAllNowSelected = newValue.includes("-1") && newValue[newValue.length - 1] === "-1";

        if (isAllNowSelected) {
            setter(["-1"]);
        } else if (wasAllSelected) {
            setter(newValue.filter(v => v !== "-1"));
        } else if (newValue.length === 0) {
            setter(["-1"]);
        } else {
            setter(newValue);
        }
    };

    const fetchReportData = async () => {
        setTableLoading(true);
        try {
            const requestBody = {
                startDate: startDate?.toISOString(),
                endDate: endDate?.toISOString(),
                gwpType: gwpValue,
                locationNames,
                materialTypes,
                locationTypes,
                regionDivisions,
                states: stateFilters,
                classifications,
                applicableRegulations,
            };

            const response = await fetch(Routes.PowerBI.IIEE(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) throw new Error('Failed to fetch report data');

            const data = await response.json();
            setReportData(data.rows || []);
            setSummaryData(data.summary || {
                startWeightLbs: 0,
                startWeightMt: 0,
                endWeightLbs: 0,
                endWeightMt: 0,
                differenceLbs: 0,
                differenceMt: 0,
                potentialEmissions: 0,
            });
        } catch (error) {
            console.error('Error fetching report data:', error);
        } finally {
            setTableLoading(false);
        }
    };

    if (loading) {
        return <LoadingBar />;
    }

    return (
        <Box sx={{ width: '100%' }}>
            {/* Filters Section */}
            <Box sx={{ mb: 4 }}>
                {/* Filter Grid */}
                <Grid container spacing={2}>
                    {/* Row 1: Start Date, End Date, GWP Value, Location Name */}
                    <Grid item xs={12} sm={6} md={3}>
                        <DateInput
                            date={startDate}
                            onChange={handleStartDateChange}
                            label="Start Date"
                            variant="standard"
                            allowPast={true}
                            allowFuture={false}
                            maxDate={endDate}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DateInput
                            date={endDate}
                            onChange={handleEndDateChange}
                            label="End Date"
                            variant="standard"
                            allowPast={true}
                            allowFuture={false}
                            minDate={startDate}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Select
                            label="GWP Value"
                            variant="standard"
                            value={gwpValue}
                            options={gwpOptions}
                            onChange={e => setGwpValue(e.target.value)}
                            showNullOption={false}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MultiSelect
                            label="Location Name"
                            variant="standard"
                            value={locationNames}
                            options={locationOptions}
                            onChange={handleMultiSelectChange(setLocationNames)}
                        />
                    </Grid>

                    {/* Row 2: Material Type, Location Type, Region/Division, Classification */}
                    <Grid item xs={12} sm={6} md={3}>
                        <MultiSelect
                            label="Material Type"
                            variant="standard"
                            value={materialTypes}
                            options={materialTypeOptions}
                            onChange={handleMultiSelectChange(setMaterialTypes)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MultiSelect
                            label="Location Type"
                            variant="standard"
                            value={locationTypes}
                            options={locationTypeOptions}
                            onChange={handleMultiSelectChange(setLocationTypes)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MultiSelect
                            label="Region/Division"
                            variant="standard"
                            value={regionDivisions}
                            options={regionDivisionOptions}
                            onChange={handleMultiSelectChange(setRegionDivisions)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MultiSelect
                            label="Classification"
                            variant="standard"
                            value={classifications}
                            options={classificationOptions}
                            onChange={handleMultiSelectChange(setClassifications)}
                        />
                    </Grid>

                    {/* Row 3: State, Applicable Regulation */}
                    <Grid item xs={12} sm={6} md={3}>
                        <MultiSelect
                            label="State"
                            variant="standard"
                            value={stateFilters}
                            options={stateOptions}
                            onChange={handleMultiSelectChange(setStateFilters)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MultiSelect
                            label="Applicable Regulation"
                            variant="standard"
                            value={applicableRegulations}
                            options={applicableRegulationOptions}
                            onChange={handleMultiSelectChange(setApplicableRegulations)}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Summary Table */}
            <SummaryTable data={summaryData} loading={tableLoading} />

            {/* Results Section - Hierarchical Table with Expansion */}
            <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 4, mb: 2, fontSize: 16 }}>
                Results — Locations
            </Typography>
            <ResultsTable data={reportData} loading={tableLoading} />
        </Box>
    );
};

export default InstalledInventoryEmissionsExposure;
